// hooks/usePaginatedList.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeMeta, buildListParams } from "../utils/pagination";

/**
 * usePaginatedList
 * @param {Function} fetcher
 * @param {Object} options   
 */
export default function usePaginatedList(fetcher, {
  initialPage = 1,
  initialPageSize = 25,
  deps = [],              
} = {}) {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(() => normalizeMeta({}, { page, page_size: pageSize }, 0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = useCallback(({ page: p = page, page_size: ps = pageSize } = {}) => {
    setPage(p);
    setPageSize(ps);
  }, [page, pageSize]);

  const getRequestParams = useCallback((extra = {}) => {
    return buildListParams({ page, page_size: pageSize, ...extra });
  }, [page, pageSize]);

  const refetch = useCallback(async (extra = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = getRequestParams(extra);
      const data = await fetcher(params);
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];
      const nextMetaRaw = Array.isArray(data) ? undefined : data?.meta;
      const nextMeta = normalizeMeta(nextMetaRaw, params, items.length);

      setRows(items);
      setMeta(nextMeta);
      return { items, meta: nextMeta };
    } catch (e) {
      setRows([]);
      setMeta(normalizeMeta({}, { page, page_size: pageSize }, 0));
      setError(String(e?.response?.data?.detail || e?.message || "Failed to load list"));
      return { items: [], meta };
    } finally {
      setLoading(false);
    }
  }, [fetcher, getRequestParams, page, pageSize]);

  useEffect(() => {
    refetch();
  }, [page, pageSize, ...deps]);

  const reloadCurrentPageSafe = useCallback(async (extra = {}) => {
    const { items } = await refetch(extra);
    const hasTotal = meta.total !== undefined;
    if (!loading && items.length === 0 && (hasTotal ? meta.page > 1 : true)) {
      onChange({ page: Math.max(1, (meta?.page || 2) - 1), page_size: meta?.page_size || pageSize });
    }
  }, [refetch, meta, loading, onChange, pageSize]);

  return {
    // state
    rows, meta, page, pageSize, loading, error,
    // actions
    onChange, getRequestParams, refetch, reloadCurrentPageSafe,
    withFilters: (filters) => ({ requestParams: getRequestParams(filters), filters }),
  };
}
